"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface GameQualitySettings {
    backdropBlur: boolean;
}

const GAME_QUALITY_KEY = 'eorguessr_game_quality';
const DEFAULT_SETTINGS: GameQualitySettings = {
    backdropBlur: true,
};

interface GameQualityContextType {
    settings: GameQualitySettings;
    setSettings: (settings: Partial<GameQualitySettings>) => void;
    isLoaded: boolean;
    getBackdropBlurClass: () => string;
    getBackdropBlur: (fallback?: string) => string;
    withBackdropBlur: (baseClasses: string, blurIntensity?: 'sm' | 'md' | 'lg' | 'xl') => string;
    getBackdropBlurIntensity: (intensity?: 'sm' | 'md' | 'lg' | 'xl') => string;
    conditionalBackdropBlur: (enabled: boolean, intensity?: 'sm' | 'md' | 'lg' | 'xl') => string;
}

const GameQualityContext = createContext<GameQualityContextType | undefined>(undefined);

interface GameQualityProviderProps {
    children: ReactNode;
}

export const GameQualityProvider: React.FC<GameQualityProviderProps> = ({ children }) => {
    const [settings, setSettingsState] = useState<GameQualitySettings>(DEFAULT_SETTINGS);
    const [isLoaded, setIsLoaded] = useState(false);

    // Load settings from localStorage on mount
    useEffect(() => {
        try {
            const savedSettings = localStorage.getItem(GAME_QUALITY_KEY);
            if (savedSettings) {
                const parsedSettings = JSON.parse(savedSettings);
                setSettingsState({ ...DEFAULT_SETTINGS, ...parsedSettings });
            }
        } catch (error) {
            console.warn('Failed to load game quality settings from localStorage:', error);
        }
        setIsLoaded(true);
    }, []);

    // Update settings and save to localStorage
    const setSettings = (newSettings: Partial<GameQualitySettings>) => {
        const updatedSettings = { ...settings, ...newSettings };
        console.log('Game quality settings changed to:', updatedSettings); // Debug log
        setSettingsState(updatedSettings);
        try {
            localStorage.setItem(GAME_QUALITY_KEY, JSON.stringify(updatedSettings));
        } catch (error) {
            console.warn('Failed to save game quality settings to localStorage:', error);
        }
    };


    // Get the backdrop blur class based on current settings
    const getBackdropBlurClass = () => {
        return settings.backdropBlur ? 'backdrop-blur-sm' : '';
    };

    // Additional backdrop blur utility methods
    const getBackdropBlur = (fallback: string = '') => {
        return settings.backdropBlur ? getBackdropBlurClass() : fallback;
    };

    const withBackdropBlur = (baseClasses: string, blurIntensity: 'sm' | 'md' | 'lg' | 'xl' = 'sm') => {
        const blurClass = settings.backdropBlur ? `backdrop-blur-${blurIntensity}` : '';
        return `${baseClasses} ${blurClass}`.trim();
    };

    const getBackdropBlurIntensity = (intensity: 'sm' | 'md' | 'lg' | 'xl' = 'sm') => {
        return settings.backdropBlur ? `backdrop-blur-${intensity}` : '';
    };

    const conditionalBackdropBlur = (enabled: boolean, intensity: 'sm' | 'md' | 'lg' | 'xl' = 'sm') => {
        return enabled ? `backdrop-blur-${intensity}` : '';
    };

    const value: GameQualityContextType = {
        settings,
        setSettings,
        isLoaded,
        getBackdropBlurClass,
        getBackdropBlur,
        withBackdropBlur,
        getBackdropBlurIntensity,
        conditionalBackdropBlur
    };

    return (
        <GameQualityContext.Provider value={value}>
            {children}
        </GameQualityContext.Provider>
    );
};

export const useGameQuality = (): GameQualityContextType => {
    const context = useContext(GameQualityContext);
    if (context === undefined) {
        throw new Error('useGameQuality must be used within a GameQualityProvider');
    }
    return context;
};